import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formsResponseSchema } from '@unit/shared';
import { Link } from 'react-router-dom';
import { FormRow } from '../features/issuance/FormsPage.js';
import { GearCard } from '../features/gear/GearCard.js';
import { useAuth } from '../features/auth/AuthProvider.js';
import { useClaimRoles } from '../features/auth/useClaimRoles.js';
import { useMyProfile } from '../features/auth/useMyProfile.js';
import { useApi } from '../lib/useApi.js';
import { Field } from './Field.js';
import { personName } from '@unit/shared';

/**
 * The soldier's home screen.
 *
 * Pending signature forms at the top (§2.6), then the soldier's own profile
 * and roles, then their gear. The Phase 0 "server connection" card that lived
 * here is gone (owner, 2026-09-13): `/api/health` still exists for the deploy
 * probe and the e2e suite, it just is not something a soldier needs to see.
 */
export function Home() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { roles: claimRoles } = useClaimRoles();
  const pending = useApi('/forms?status=pending_signature', formsResponseSchema);

  const profile = useMyProfile(user?.uid ?? null, claimRoles);
  const displayName = useMemo(() => {
    if (profile.kind !== 'ready') return '';
    return personName(profile.profile);
  }, [profile]);

  return (
    <>
      {pending.kind === 'ready' && pending.data.forms.length > 0 && (
        <section
          className="rounded-2xl bg-amber-50 p-5 shadow-sm ring-1 ring-amber-200"
          data-testid="pending-forms"
        >
          <h2 className="font-semibold text-amber-900">
            {t('home.pendingForms', { count: pending.data.forms.length })}
          </h2>
          <ul className="mt-2 divide-y divide-amber-100">
            {pending.data.forms.map((form) => (
              <FormRow
                key={form.id}
                form={form}
                language={i18n.language}
                showSoldier={form.soldierUid !== user?.uid}
              />
            ))}
          </ul>
          <Link to="/forms" className="mt-2 inline-block text-sm text-amber-800 underline-offset-4 hover:underline">
            {t('home.allForms')}
          </Link>
        </section>
      )}

      <section
        className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200"
        data-testid="profile-card"
      >
        {profile.kind === 'loading' && <p className="text-slate-500">{t('home.loading')}</p>}
        {profile.kind === 'error' && (
          <p role="alert" className="text-sm text-red-600">
            {profile.message}
          </p>
        )}
        {profile.kind === 'ready' && (
          <>
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <h2 className="text-xl font-semibold text-slate-900" data-testid="greeting">
                {t('home.greeting', { name: displayName })}
              </h2>
              <Link to="/me" className="text-sm text-slate-600 underline-offset-4 hover:underline" data-testid="my-file-link">
                {t('nav.me')}
              </Link>
            </div>
            <dl className="mt-4 divide-y divide-slate-100">
              <Field label={t('home.yourRoles')}>
                <span data-testid="roles">
                  {profile.profile.roles.map((role) => t(`roles.${role}`)).join(' · ')}
                </span>
              </Field>
              <Field label={t('home.personalNumber')} latin>
                {profile.profile.personalNumber}
              </Field>
              <Field label={t('home.platoon')} latin>
                {profile.profile.platoonId ?? '—'}
              </Field>
              <Field label={t('home.team')} latin>
                {profile.profile.teamId ?? '—'}
              </Field>
            </dl>
          </>
        )}
      </section>

      {user && <GearCard uid={user.uid} title={t('gear.myTitle')} />}
    </>
  );
}
